//
// File: IShellPluginConfig.ts
// Description: Type definition for a Company
// Copyright: Broadlume 2024
//

import { TenantPluginFeature } from '../api/Plugin';

/**
 * ShellPluginType, representing the type of shell plugin
 */
export enum ShellPluginType {
  APPLICATION = 'application',
  SERVICE = 'service',
  HOST = 'host',
}

export enum ShellPluginCategory {
  PERFORMANCE = 'performance',
  CATALOG = 'catalog',
  WEBSITE = 'website',
  CRM = 'crm',
  COMPANY = 'company',
  ADMIN = 'admin',
}

export enum ShellPluginAvailableTo {
  INTERNAL = 'internal',
  BOTH = 'both',
}

/**
 * Plugin display labels, keyed by locale (e.g. { en: 'Catalog', es: 'Catálogo' }).
 *
 * Plugins are created at runtime, so their names cannot live in the build-time
 * i18n bundles the rest of the UI copy uses -- these labels are the single source
 * of truth for the translated plugin name. Every locale is optional; resolution
 * falls back to English and then to the plugin's `name`.
 *
 * Read this through `usePluginLabel` / `resolvePluginLabel` rather than indexing
 * it directly, so the fallback chain is applied consistently.
 */
export type PluginLabel = Partial<Record<string, string>>;

/**
 * Provenance of a label filled in by the translation service. `sourceText` is the
 * English label it was translated from.
 */
export interface PluginLabelMachineTranslation {
  model: string;
  translatedAt: string;
  sourceText: string;
}

/**
 * Translation settings for a plugin's labels.
 *
 * - `exclude`: keep the plugin's name untranslated (brand and product names).
 * - `machineTranslated`: locales that were machine translated and not yet reviewed.
 *   Remove a locale's entry to accept its translation; editing the label does the
 *   same. The server never lets a client add an entry.
 */
export interface PluginLabelTranslationSettings {
  exclude?: boolean;
  machineTranslated?: Partial<Record<string, PluginLabelMachineTranslation>>;
}

export interface ConfigurationSchema {
  /**
   * Per-locale display labels for the plugin. Absent on plugins created before
   * labels existed, which is why resolution falls back to `name`.
   */
  label?: PluginLabel;
  /** Translation settings and machine-translation provenance for `label`. */
  labelTranslation?: PluginLabelTranslationSettings;
  features: {
    id?: string;
    name: string;
    description?: string;
    inputType?: string;
    permissions: {
      name: string;
      editable: boolean;
    }[];
    custom?: Record<string, string | boolean | number>;
  }[];
  advancedFeatures: {
    id?: string;
    name: string;
    description?: string;
    inputType?: string;
    permissions: {
      editable: boolean;
    }[];
    custom?: Record<string, string | boolean | number>;
  }[];
  notifications: {
    id?: string;
    triggers: string[];
    channel: string[];
    recipientType?: 'dealer' | 'consumer';
  }[];
}

/**
 * ShellPluginComponentType, representing the type of react shell component to load the src
 */
export enum ShellPluginComponentType {
  /**
   * The plugins is a webpacked react application
   */
  WEBPACKED = 'webpacked',
  /**
   * The plugin is an iframe
   */
  IFRAME = 'iframe',
  /**
   * The plugin is a simple link to an external site
   */
  EXTERNAL_LINK = 'externalLink',
  /**
   * Plugin has no displayable component
   */
  NONE = 'none',
}

/**
 * IShellPluginConfig type, representing a shell plugin configuration
 */
export interface IShellPluginConfig {
  /**
   * The unique identifier for the plugin
   */
  id: string;
  /**
   * The name of the plugin 'plugin1'
   */
  name: string;
  /**
   * Who can access this plugin: 'internal' or 'both'
   */
  availableTo?: ShellPluginAvailableTo;
  /**
   * If true, plugin is only visible when a company is selected (hidden in System Admin view)
   */
  companyScopedOnly?: boolean;
  /**
   * The type of plugin 'application', 'service', 'host'
   */
  type: ShellPluginType;
  /**
   * The type of react shell component to load the src (only applicable if type is 'application'),
   */
  componentType?: ShellPluginComponentType;
  /**
   * The base route used for plugin lookup and routing (only applicable if type is 'application')
   */
  shellRoute?: string;
  /**
   * The description of plugin 'plugin1 description'
   */
  description?: string;
  /**
   * The version of plugin '1.0.0'
   */
  version?: string;
  /**
   * The url to remote webpack container entry point 'http://localhost:4201' (currently code will append '/remoteEntry.js' to url), or iframe src url
   */
  src: string;
  /**
   * The name used in the webpack module federation configuration
   */
  webpackName?: string;
  /**
   * The module name within webpack container located at src './Module'
   */
  module?: string;
  /**
   * The css module name within webpack container located at src './Css'
   */
  css?: string;
  /**
   * Source path to proxy requests from e.g. '/plugin1'
   */
  proxyFrom?: string;
  /**
   * Destination path to proxy requests to 'http://localhost:4201/plugin1'
   */
  proxyTo?: string;
  /**
   * Friendly message to display if plugin fails to load
   */
  importError: string;
  /**
   * Icon to display in launch menu (image url)
   */
  launchIcon?: string;
  /**
   * Broadlume product id this plugin is associated with
   */
  blProductId?: string;
  /**
   * Broadlume product name this plugin is associated with
   */
  blProductName?: string;
  /**
   * Configuration object for the plugin
   */
  configuration?: ConfigurationSchema;

  /**
   * Category of the plugin
   */
  category?: string;
  /**
   * Features of the plugin
   */
  pluginFeatures?: TenantPluginFeature[];
}

/**
 * PluginAccessItem, representing plugin access information
 */
export interface PluginAccessItem {
  id: string;
  name: string;
  hasAccess: boolean;
  tenantPluginId: string | null;
}
